import React, { Fragment, useState, useEffect } from "react";
import PageTitle from "../../layouts/PageTitle";
import swal from "sweetalert";
import api, { getFileUrl } from "../../../api";
import { useDispatch } from "react-redux";

const BACKEND_URL = process.env.REACT_APP_API_URL;

const Profile = () => {
 const dispatch = useDispatch();
 const [userData, setUserData] = useState(null);
 const [activeTab, setActiveTab] = useState("profile");

 const [formData, setFormData] = useState({
 name: "",
 email: "",
 mobile: "",
 });

 const [file, setFile] = useState(null);
 const [loading, setLoading] = useState(false);

 const [signatureFile, setSignatureFile] = useState(null);
 const [signatureLoading, setSignatureLoading] = useState(false);

 const [passwordData, setPasswordData] = useState({
 currentPassword: "",
 newPassword: "",
 confirmPassword: "",
 });

 const [loadingPwd, setLoadingPwd] = useState(false);

 useEffect(() => {
 fetchUserProfile();
 }, []);

 const fetchUserProfile = async () => {
 try {
 const res = await api.get("/auth/me");
 const data = res.data;

 if (data.data) {
 setUserData(data.data);
 setFormData({
 name: data.data.name || "",
 email: data.data.email || "",
 mobile: data.data.mobile || "",
 });
 } else {
 swal("Error", data.message || "Failed to fetch profile", "error");
 }
 } catch (err) {
 swal("Error", err.message, "error");
 }
 };

 const handleChange = (e) => {
 const { name, value } = e.target;
 setFormData((prev) => ({ ...prev, [name]: value }));
 };

 const handleFileChange = (e) => {
 const selectedFile = e.target.files[0];
 if (selectedFile) setFile(selectedFile);
 };

 const handleSignatureFileChange = (e) => {
 const selectedFile = e.target.files[0];
 if (selectedFile) setSignatureFile(selectedFile);
 };

 const handleSubmitBasic = async (e) => {
 e.preventDefault();
 setLoading(true);

 try {
 const fd = new FormData();
 fd.append("name", formData.name);
 fd.append("email", formData.email);
 fd.append("mobile", formData.mobile);
 if (file) fd.append("image", file);

 const res = await api.patch(`/users/${userData._id}`, fd);
 if (res.data.success) {
 swal("Success!", "Profile updated successfully.", "success");
 fetchUserProfile();
 // Sync image update to redux + localStorage
 if (res.data.data?.image) {
 dispatch({ type: 'UPDATE_USER_DETAILS', payload: { image: res.data.data.image } });
 }
 setFile(null);
 } else {
 swal("Error", res.data.message || "Failed to update profile", "error");
 }
 } catch (err) {
 swal("Error", err.response?.data?.message || err.message, "error");
 } finally {
 setLoading(false);
 }
 };

 const handleSignatureUpload = async (e) => {
 e.preventDefault();
 if (!signatureFile) {
 swal("Error", "Please select a signature image first.", "error");
 return;
 }
 setSignatureLoading(true);

 try {
 const fd = new FormData();
 fd.append("signature", signatureFile);

 const res = await api.patch(`/users/${userData._id}`, fd);
 if (res.data.success) {
 swal("Success!", "Signature uploaded successfully.", "success");
 fetchUserProfile();
 // Update redux + localStorage so SignatureModal picks it up immediately
 if (res.data.data?.signature) {
 dispatch({ type: 'UPDATE_USER_DETAILS', payload: { signature: res.data.data.signature } });
 }
 setSignatureFile(null);
 // reset file input
 const input = document.getElementById("signatureInput");
 if (input) input.value = "";
 } else {
 swal("Error", res.data.message || "Failed to upload signature", "error");
 }
 } catch (err) {
 swal("Error", err.response?.data?.message || err.message, "error");
 } finally {
 setSignatureLoading(false);
 }
 };

 const handlePasswordChange = (e) => {
 const { name, value } = e.target;
 setPasswordData((prev) => ({ ...prev, [name]: value }));
 };

 const handlePasswordUpdate = async (e) => {
 e.preventDefault();
 setLoadingPwd(true);

 if (passwordData.newPassword !== passwordData.confirmPassword) {
 swal("Error", "New password & confirm password do not match!", "error");
 setLoadingPwd(false);
 return;
 }

 try {
 const res = await api.patch(`/auth/change-password`, {
 currentPassword: passwordData.currentPassword,
 newPassword: passwordData.newPassword,
 });

 if (res.data.success) {
 swal("Success!", "Password updated successfully.", "success");
 setPasswordData({ currentPassword: "", newPassword: "", confirmPassword: "" });
 } else {
 swal("Error", res.data.message || "Failed to update password", "error");
 }
 } catch (err) {
 swal("Error", err.response?.data?.message || err.message, "error");
 } finally {
 setLoadingPwd(false);
 }
 };

 if (!userData) {
 return (
 <Fragment>
 <PageTitle activeMenu="Profile" motherMenu="Users" />
 <div className="text-center mt-5"><p>Loading profile...</p></div>
 </Fragment>
 );
 }

 return (
 <Fragment>
 <PageTitle activeMenu="Profile" motherMenu="Users" />

 <div className="row g-3">

 {/* LEFT PREVIEW */}
 <div className="col-md-4">
 <div className="card shadow-sm">
 <div className="card-body text-center">
 <img
 src={
 file
 ? URL.createObjectURL(file)
 : userData.image
 ? getFileUrl(userData.image)
 : "https://via.placeholder.com/150?text=No+Image"
 }
 alt="Profile"
 className="rounded-circle border mb-3"
 style={{ width: "130px", height: "130px", objectFit: "cover" }}
 />

 <h4 className="text-dark fw-bold">{userData.name}</h4>
 <p className="text-muted mb-1">{userData.email}</p>
 <p className="text-muted mb-1">{userData.mobile}</p>
 <span className="badge bg-primary">
 {userData.role?.name || "No Role Assigned"}
 </span>

 {/* Signature Preview */}
 {(signatureFile || userData.signature) && (
 <div className="mt-4">
 <p className="text-muted mb-1" style={{ fontSize: "12px" }}>Signature</p>
 <div className="border rounded p-2 bg-light d-inline-block">
 <img
 src={
 signatureFile
 ? URL.createObjectURL(signatureFile)
 : getFileUrl(userData.signature)
 }
 alt="Signature"
 style={{ maxWidth: "180px", maxHeight: "80px", objectFit: "contain" }}
 />
 </div>
 </div>
 )}
 </div>
 </div>
 </div>

 {/* RIGHT CONTENT */}
 <div className="col-md-8">
 <div className="card shadow-sm">
 <div className="card-header pb-0">
 <ul className="nav nav-tabs">
 <li className="nav-item">
 <button
 className={`nav-link ${activeTab === "profile" ? "active" : ""}`}
 onClick={() => setActiveTab("profile")}
 >
 Update Profile
 </button>
 </li>

 <li className="nav-item">
 <button
 className={`nav-link ${activeTab === "signature" ? "active" : ""}`}
 onClick={() => setActiveTab("signature")}
 >
 Signature
 </button>
 </li>

 <li className="nav-item">
 <button
 className={`nav-link ${activeTab === "password" ? "active" : ""}`}
 onClick={() => setActiveTab("password")}
 >
 Change Password
 </button>
 </li>
 </ul>
 </div>

 <div className="card-body">

 {/* PROFILE FORM */}
 {activeTab === "profile" && (
 <form onSubmit={handleSubmitBasic}>
 <div className="mb-3">
 <label className="form-label">Name</label>
 <input type="text" name="name" className="form-control"
 value={formData.name} onChange={handleChange} required />
 </div>

 <div className="mb-3">
 <label className="form-label">Email</label>
 <input type="email" name="email" className="form-control"
 value={formData.email} onChange={handleChange} required />
 </div>

 <div className="mb-3">
 <label className="form-label">Mobile</label>
 <input type="text" name="mobile" className="form-control"
 value={formData.mobile} onChange={handleChange} required />
 </div>

 <div className="mb-3">
 <label className="form-label">Profile Image</label>
 <input type="file" className="form-control" accept="image/*" onChange={handleFileChange} />
 {file && <small className="text-success">Selected: {file.name}</small>}
 </div>

 <button className="btn btn-primary" type="submit" disabled={loading}>
 {loading ? "Updating..." : "Update Profile"}
 </button>
 </form>
 )}

 {/* SIGNATURE FORM */}
 {activeTab === "signature" && (
 <div>
 <p className="text-muted mb-3" style={{ fontSize: "13px" }}>
 Upload your signature image (JPG, PNG, GIF). This will be used in documents and work orders.
 </p>

 {/* Current Signature */}
 {userData.signature && (
 <div className="mb-4">
 <label className="form-label fw-semibold">Current Signature</label>
 <div className="border rounded p-3 bg-light text-center">
 <img
 src={getFileUrl(userData.signature)}
 alt="Current Signature"
 style={{ maxWidth: "300px", maxHeight: "120px", objectFit: "contain" }}
 />
 </div>
 </div>
 )}

 <form onSubmit={handleSignatureUpload}>
 <div className="mb-3">
 <label className="form-label">
 {userData.signature ? "Replace Signature" : "Upload Signature"}
 </label>
 <input
 id="signatureInput"
 type="file"
 className="form-control"
 accept="image/*"
 onChange={handleSignatureFileChange}
 />
 <small className="text-muted">Accepted formats: JPG, PNG, GIF</small>
 </div>

 {/* New Signature Preview */}
 {signatureFile && (
 <div className="mb-3">
 <label className="form-label">Preview</label>
 <div className="border rounded p-3 bg-light text-center">
 <img
 src={URL.createObjectURL(signatureFile)}
 alt="New Signature Preview"
 style={{ maxWidth: "300px", maxHeight: "120px", objectFit: "contain" }}
 />
 </div>
 <small className="text-success mt-1 d-block">Selected: {signatureFile.name}</small>
 </div>
 )}

 <button className="btn btn-success" type="submit" disabled={signatureLoading}>
 {signatureLoading ? "Uploading..." : userData.signature ? "Replace Signature" : "Upload Signature"}
 </button>
 </form>
 </div>
 )}

 {/* PASSWORD FORM */}
 {activeTab === "password" && (
 <form onSubmit={handlePasswordUpdate}>
 <div className="mb-3">
 <label className="form-label">Current Password</label>
 <input type="password" name="currentPassword"
 className="form-control"
 value={passwordData.currentPassword}
 onChange={handlePasswordChange} required />
 </div>

 <div className="mb-3">
 <label className="form-label">New Password</label>
 <input type="password" name="newPassword"
 className="form-control"
 value={passwordData.newPassword}
 onChange={handlePasswordChange} required />
 </div>

 <div className="mb-3">
 <label className="form-label">Confirm New Password</label>
 <input type="password" name="confirmPassword"
 className="form-control"
 value={passwordData.confirmPassword}
 onChange={handlePasswordChange} required />
 </div>

 <button className="btn btn-warning" type="submit" disabled={loadingPwd}>
 {loadingPwd ? "Updating..." : "Update Password"}
 </button>
 </form>
 )}

 </div>
 </div>
 </div>

 </div>
 </Fragment>
 );
};

export default Profile;
